'use client';

import { useState, useEffect, useRef, useCallback } from 'react';

export interface FilterOption {
  id: string;
  label: string;
  icon: string;
  children?: FilterOption[];
  /** Section header shown above this group in dropdown */
  groupLabel?: string;
}

interface CascadingFilterProps {
  options: FilterOption[];
  active: string;
  onChange: (id: string) => void;
  /** When true, drops outer mt-3 and justify-center (parent dock handles layout) */
  bare?: boolean;
  /** When true, render a single dropdown trigger instead of inline pills */
  compact?: boolean;
  /** When true, render compact on mobile, inline pills on md:+ */
  responsive?: boolean;
  ariaLabel?: string;
  /** Label shown before the filter options */
  label?: string;
}

/** Find the active option (searching top-level and children) */
function findActiveOption(options: FilterOption[], activeId: string): FilterOption | undefined {
  for (const opt of options) {
    if (opt.id === activeId) return opt;
    if (opt.children) {
      const child = opt.children.find(c => c.id === activeId);
      if (child) return child;
    }
  }
  return undefined;
}

export function CascadingFilter({ options, active, onChange, bare, compact, responsive, ariaLabel = 'Filter events', label }: CascadingFilterProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    if (!openDropdown) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenDropdown(null); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [openDropdown]);

  // Close on outside click
  useEffect(() => {
    if (!openDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openDropdown]);

  // Arrow key navigation within dropdown
  const handleDropdownKeyDown = useCallback((e: React.KeyboardEvent) => {
    const items = dropdownRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
    if (!items || items.length === 0) return;
    const currentIndex = Array.from(items).findIndex(el => el === document.activeElement);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
      items[next].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
      items[prev].focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1].focus();
    }
  }, []);

  /* ── Compact dropdown renderer (reused by compact & responsive mobile) ── */
  const renderCompactDropdown = (wrapperClassName?: string) => {
    const isOpen = openDropdown === '__compact__';
    const activeOpt = findActiveOption(options, active);
    const displayIcon = activeOpt?.icon || options[0]?.icon || '';
    const displayLabel = activeOpt?.label || options[0]?.label || 'All';

    return (
      <div className={`relative ${wrapperClassName || ''}`} ref={isOpen ? dropdownRef : undefined}>
        <button
          onClick={() => setOpenDropdown(isOpen ? null : '__compact__')}
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-label={ariaLabel}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
        >
          <span>{displayIcon}</span>
          {label && <span className="text-text-tertiary">{label}:</span>}
          <span>{displayLabel}</span>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}>
            <path d="M2.5 4 L5 6.5 L7.5 4" />
          </svg>
        </button>

        {isOpen && (
          <div
            role="menu"
            className="absolute left-0 top-full z-50 mt-1 max-h-64 min-w-[200px] overflow-y-auto content-card p-1 shadow-lg animate-in fade-in slide-in-from-top-1 duration-150"
            onKeyDown={handleDropdownKeyDown}
          >
            {options.map(opt => {
              const hasChildren = opt.children && opt.children.length > 0;

              if (hasChildren) {
                return (
                  <div key={opt.id}>
                    {opt.groupLabel && (
                      <div className="px-3 pt-2 pb-1 text-caption font-semibold uppercase tracking-wider text-muted">{opt.groupLabel}</div>
                    )}
                    <div className="px-3 pt-2 pb-1 text-caption font-semibold uppercase tracking-wider text-muted">{opt.label}</div>
                    {opt.children!.map(child => (
                      <button
                        key={child.id}
                        role="menuitem"
                        onClick={() => {
                          onChange(child.id);
                          setOpenDropdown(null);
                        }}
                        className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-surface-hover ${
                          child.id === active
                            ? 'font-semibold text-text-primary'
                            : 'text-text-secondary'
                        }`}
                      >
                        <span className="text-base">{child.icon}</span>
                        <span className="truncate">{child.label}</span>
                      </button>
                    ))}
                  </div>
                );
              }

              return (
                <button
                  key={opt.id}
                  role="menuitem"
                  onClick={() => {
                    onChange(opt.id);
                    setOpenDropdown(null);
                  }}
                  className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-surface-hover ${
                    opt.id === active
                      ? 'font-semibold text-text-primary'
                      : 'text-text-secondary'
                  }`}
                >
                  <span className="text-base">{opt.icon}</span>
                  <span className="truncate">{opt.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  /* ── Inline pills renderer (reused by default & responsive desktop) ── */
  const renderInlinePills = (wrapperClassName?: string) => (
    <div role="toolbar" aria-label={ariaLabel} className={wrapperClassName || (bare ? 'flex flex-wrap gap-1.5' : 'mt-3 flex flex-wrap justify-center gap-1.5')}>
      {label && <span className="text-xs text-text-tertiary font-medium self-center mr-0.5">{label}</span>}
      {options.map(opt => {
        const hasChildren = opt.children && opt.children.length > 0;
        const isParentActive = hasChildren && opt.children!.some(c => c.id === active);
        const isActive = opt.id === active || isParentActive;
        const isOpen = openDropdown === opt.id;

        return (
          <div key={opt.id} className="relative" ref={isOpen ? dropdownRef : undefined}>
            <button
              onClick={() => {
                if (hasChildren) {
                  setOpenDropdown(isOpen ? null : opt.id);
                } else {
                  onChange(opt.id);
                  setOpenDropdown(null);
                }
              }}
              aria-pressed={isActive}
              aria-expanded={hasChildren ? isOpen : undefined}
              aria-haspopup={hasChildren ? 'true' : undefined}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                isActive
                  ? 'bg-brand text-text-on-brand'
                  : 'border border-border-primary bg-surface-card text-text-secondary hover:bg-surface-hover hover:text-text-primary'
              }`}
            >
              <span>{opt.icon}</span> {opt.label}
              {hasChildren && (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                  <path d="M2.5 4 L5 6.5 L7.5 4" />
                </svg>
              )}
            </button>

            {hasChildren && isOpen && (
              <div
                role="menu"
                className="absolute left-1/2 top-full z-50 mt-1 max-h-56 min-w-[200px] -translate-x-1/2 overflow-y-auto rounded-xl border border-border-subtle bg-surface-elevated shadow-lg"
                onKeyDown={handleDropdownKeyDown}
              >
                {opt.groupLabel && (
                  <div className="px-3 pt-2 pb-1 text-caption font-semibold uppercase tracking-wider text-muted">{opt.groupLabel}</div>
                )}
                {opt.children!.map(child => {
                  const childGroupHeader = child.groupLabel ? (
                    <div className="px-3 pt-2 pb-1 text-caption font-semibold uppercase tracking-wider text-muted">{child.groupLabel}</div>
                  ) : null;

                  return (
                    <div key={child.id}>
                      {childGroupHeader}
                      <button
                        role="menuitem"
                        onClick={() => {
                          onChange(child.id);
                          setOpenDropdown(null);
                        }}
                        className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-surface-hover ${
                          child.id === active
                            ? 'font-semibold text-text-primary'
                            : 'text-text-secondary'
                        }`}
                      >
                        <span className="text-base">{child.icon}</span>
                        <span className="truncate">{child.label}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  /* ── Compact mode: single dropdown trigger ── */
  if (compact) {
    return renderCompactDropdown();
  }

  /* ── Responsive mode: compact on mobile, inline pills on md:+ ── */
  if (responsive) {
    return (
      <>
        {renderCompactDropdown('md:hidden')}
        {renderInlinePills('hidden md:flex flex-wrap items-center gap-1.5')}
      </>
    );
  }

  /* ── Default mode: inline pills ── */
  return renderInlinePills();
}
